/**
 * rollingDates.ts
 * -----------------------------------------------------------------------------
 * The **rolling-date filter-variable grammar** and the calendar arithmetic that
 * resolves it.
 *
 * A rolling date is an `@`-token that names a moving point on the calendar. It
 * is resolved SERVER-SIDE to a literal value before any engine (in-memory,
 * Gremlin, Elasticsearch) sees the filter, exactly like `@me` / `@today` — see
 * `resolveFilterVariables.ts` in graph-service. Extending the TOKEN vocabulary
 * rather than the `FilterOperator` union is deliberate: a rolling date then
 * composes with EVERY existing date operator, so `less_than @today-30d` is
 * "older than 30 days" and `between @startOfQuarter .. @endOfQuarter` is "this
 * quarter", with no new operator in any engine's operator map.
 *
 * ## Grammar
 *
 *     token   := anchor [ offset ]
 *     anchor  := '@today'
 *              | '@startOfWeek'    | '@endOfWeek'
 *              | '@startOfMonth'   | '@endOfMonth'
 *              | '@startOfQuarter' | '@endOfQuarter'
 *              | '@startOfYear'    | '@endOfYear'
 *     offset  := sign amount unit
 *     sign    := '+' | '-'
 *     amount  := '0' | [1-9][0-9]{0,3}      (no leading zeros — see below)
 *     unit    := 'd' | 'w' | 'm' | 'q' | 'y'
 *
 * Examples: `@today`, `@today-7d`, `@today+30d`, `@today-1y`,
 * `@startOfMonth-1m` (start of last month), `@startOfQuarter-1q`,
 * `@endOfWeek+2w`.
 *
 * `amount` forbids leading zeros so every rolling date has exactly ONE spelling.
 * That matters beyond tidiness: the token string is scanned verbatim into the
 * Redis cache key's variable-binding segment, so `@today-07d` and `@today-7d`
 * would key two cache entries for one filter.
 *
 * ## Evaluation order: offset moves the reference point, the anchor snaps it
 *
 * `resolveRollingDate` applies the OFFSET to "today" first, then snaps the
 * result to the ANCHOR. This is the only order that makes every combination
 * mean what its name says:
 *
 *   `@startOfMonth-1m`  today − 1 month, then start of that month  = start of last month
 *   `@endOfMonth+1m`    today + 1 month, then end of that month    = end of next month
 *   `@endOfQuarter-1q`  today − 1 quarter, then end of that quarter = end of last quarter
 *
 * Anchor-then-offset would get the `start*` family right by luck and the `end*`
 * family wrong: end-of-February (28th) plus one month is 28 March, not the end
 * of March.
 *
 * ## Calendar arithmetic is REAL — a month is not 30 days
 *
 * Offsets in `d`/`w` are exact day counts (a week is always 7 civil days).
 * Offsets in `m`/`q`/`y` are CALENDAR month arithmetic — `q` is 3 months, `y`
 * is 12 months — never a fixed number of days. All arithmetic runs on a civil
 * `{year, month, day}` triple, so leap years and month lengths come from the
 * real calendar via `daysInMonth`.
 *
 * ### Clamping rule for month/quarter/year offsets: CONSTRAIN to month end
 *
 * When shifting by whole months lands on a day that does not exist in the
 * target month, the day is clamped DOWN to that month's last day:
 *
 *     2026-01-31 + 1m  ->  2026-02-28      (not 2026-03-03)
 *     2028-01-31 + 1m  ->  2028-02-29      (leap year)
 *     2028-02-29 + 1y  ->  2029-02-28
 *     2026-03-31 - 1m  ->  2026-02-28
 *
 * Chosen over overflow-into-the-next-month for two reasons. First, it is what
 * "a month later" means to the person who authored the filter: the result stays
 * inside the month they named, so `@today-1m` on 31 March cannot silently
 * report as 3 March. Second, it is the `constrain` behaviour of ECMAScript
 * Temporal's `PlainDate.add` (and of Luxon's `plus({months})`), so a future
 * migration onto either is behaviour-preserving rather than a silent shift in
 * what every saved filter selects.
 *
 * Note that clamping is NOT reversible: `2026-01-31 + 1m - 1m` is `2026-01-28`,
 * not `2026-01-31`. That is inherent to calendar arithmetic, not a defect —
 * every implementation that clamps has it, and the alternative (overflow) is
 * not reversible either.
 *
 * ## Week starts Monday (ISO 8601), never locale-derived
 *
 * `@startOfWeek` is the Monday of the reference week and `@endOfWeek` the
 * following Sunday, on every host and for every viewer. A locale-derived week
 * start would make one saved filter select different spheres for two
 * colleagues, which is a correctness bug, not a localisation feature.
 *
 * ## Time zone
 *
 * The calendar arithmetic above is pure civil-date maths and needs no zone.
 * The zone enters at exactly two points, both here:
 *
 *  - `civilDateInZone` — deciding what "today" IS for the viewer. This is the
 *    input to `resolveRollingDate`.
 *  - `zonedStartOfDayIso` / `zonedEndOfDayIso` — turning a resolved civil date
 *    back into the exact UTC instants that bound that day in the viewer's zone,
 *    for a `between` filter's `min`/`max`.
 *
 * Both are implemented against `Intl.DateTimeFormat`'s IANA time-zone database,
 * so DST transitions, historical offset changes and sub-hour offsets are the
 * platform's problem rather than ours. This module deliberately takes NO
 * runtime dependency (no Luxon): `@center-platform/spheres` is bundled for the
 * browser as well as the services.
 */

export const ROLLING_DATE_ANCHORS = [
  '@today',
  '@startOfWeek',
  '@endOfWeek',
  '@startOfMonth',
  '@endOfMonth',
  '@startOfQuarter',
  '@endOfQuarter',
  '@startOfYear',
  '@endOfYear',
] as const;

export type RollingDateAnchor = (typeof ROLLING_DATE_ANCHORS)[number];

export const ROLLING_DATE_UNITS = ['d', 'w', 'm', 'q', 'y'] as const;

export type RollingDateUnit = (typeof ROLLING_DATE_UNITS)[number];

/**
 * Every string the grammar accepts. The template-literal form keeps narrowing
 * precise for consumers (`isFilterVariable`'s type predicate) without
 * enumerating an infinite set.
 */
export type RollingDateToken =
  | RollingDateAnchor
  | `${RollingDateAnchor}${'+' | '-'}${number}${RollingDateUnit}`;

export interface ParsedRollingDate {
  anchor: RollingDateAnchor;
  /** `+1` or `-1`; always `+1` when there is no offset. */
  sign: 1 | -1;
  /** Offset magnitude; `0` when there is no offset. */
  amount: number;
  /** `null` when the token is a bare anchor. */
  unit: RollingDateUnit | null;
}

/**
 * The one regular expression that defines the grammar. Anchored at both ends —
 * a token is a WHOLE filter value, never a substring of one, so a property
 * value that merely starts with `@today` is not a token.
 *
 * `(?:0|[1-9]\d{0,3})` forbids leading zeros and caps the magnitude at 9999,
 * which bounds the arithmetic (a 4-digit year offset is ~10 000 years — well
 * inside the ECMAScript date range, so no offset the grammar accepts can
 * produce an invalid date).
 */
const ROLLING_DATE_PATTERN =
  /^(@today|@startOfWeek|@endOfWeek|@startOfMonth|@endOfMonth|@startOfQuarter|@endOfQuarter|@startOfYear|@endOfYear)(?:([+-])(0|[1-9]\d{0,3})([dwmqy]))?$/;

/**
 * Parses a rolling-date token. Returns `null` for anything the grammar does not
 * accept — a bare `@me`, an unknown anchor, a missing or unknown unit, a
 * leading-zero amount, a fractional amount, or a token with trailing junk.
 *
 * `null` is a REFUSAL, not a default: every caller must treat it as "this is
 * not a rolling date", never as "resolve it to today". Letting a malformed
 * token fall through to a real date would silently widen or shift a filter.
 */
export function parseRollingDateToken(value: unknown): ParsedRollingDate | null {
  if (typeof value !== 'string') return null;

  const match = ROLLING_DATE_PATTERN.exec(value);
  if (!match) return null;

  const [, anchor, sign, amount, unit] = match;

  return {
    anchor: anchor as RollingDateAnchor,
    sign: sign === '-' ? -1 : 1,
    amount: unit ? Number(amount) : 0,
    unit: (unit as RollingDateUnit) ?? null,
  };
}

/** True when `value` is a syntactically valid rolling-date token. */
export function isRollingDateToken(value: unknown): value is RollingDateToken {
  return parseRollingDateToken(value) !== null;
}

// ---------------------------------------------------------------------------
// Civil-date arithmetic (no zone, no instant — just {year, month, day})
// ---------------------------------------------------------------------------

interface CivilDate {
  /** Full year, e.g. 2026. */
  year: number;
  /** 1-12, NOT the JS 0-11 month index. */
  month: number;
  /** 1-31. */
  day: number;
}

const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses a bare `YYYY-MM-DD` civil date. Throws rather than returning a
 * sentinel: every caller inside this module is handed a date this module (or
 * `civilDateInZone`) produced, so a parse failure is a programming error, and a
 * silently-substituted "today" would corrupt a filter without a trace.
 */
function parseCivilDate(value: string): CivilDate {
  const match = CIVIL_DATE_PATTERN.exec(value);
  if (!match) {
    throw new Error(`[rollingDates] Not a YYYY-MM-DD civil date: ${JSON.stringify(value)}`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`[rollingDates] Not a real calendar date: ${value}`);
  }
  return { year, month, day };
}

function formatCivilDate(date: CivilDate): string {
  const mm = String(date.month).padStart(2, '0');
  const dd = String(date.day).padStart(2, '0');
  return `${String(date.year).padStart(4, '0')}-${mm}-${dd}`;
}

/**
 * Days in a (1-based) month of a given year, from the real calendar.
 * `Date.UTC(year, month, 0)` is day zero of the NEXT month index, i.e. the last
 * day of this one — so February returns 29 in a leap year (including the
 * century rule: 2000 has 29, 1900 has 28) with no leap-year branch here.
 */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Exact day arithmetic. UTC has no DST, so a UTC day is always 86 400 000 ms. */
function addDays(date: CivilDate, days: number): CivilDate {
  const ms = Date.UTC(date.year, date.month - 1, date.day) + days * 86_400_000;
  const shifted = new Date(ms);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/**
 * Calendar month arithmetic with day-of-month CONSTRAINED to the target month's
 * last day (see the module header for why constrain rather than overflow).
 */
function addMonths(date: CivilDate, months: number): CivilDate {
  const total = date.year * 12 + (date.month - 1) + months;
  const year = Math.floor(total / 12);
  // `total % 12` is negative in JS for a negative `total`; `Math.floor` above
  // already took the correct year, so recover the month from the difference.
  const month = total - year * 12 + 1;
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

/** ISO 8601 day of week: 1 = Monday … 7 = Sunday. Never locale-derived. */
function isoDayOfWeek(date: CivilDate): number {
  const jsDay = new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
}

/** 1-based first month of the quarter containing `month`: 1, 4, 7 or 10. */
function firstMonthOfQuarter(month: number): number {
  return Math.floor((month - 1) / 3) * 3 + 1;
}

const ANCHOR_SNAPS: Record<RollingDateAnchor, (date: CivilDate) => CivilDate> = {
  '@today': (date) => date,
  '@startOfWeek': (date) => addDays(date, -(isoDayOfWeek(date) - 1)),
  '@endOfWeek': (date) => addDays(date, 7 - isoDayOfWeek(date)),
  '@startOfMonth': (date) => ({ ...date, day: 1 }),
  '@endOfMonth': (date) => ({ ...date, day: daysInMonth(date.year, date.month) }),
  '@startOfQuarter': (date) => ({ ...date, month: firstMonthOfQuarter(date.month), day: 1 }),
  '@endOfQuarter': (date) => {
    const month = firstMonthOfQuarter(date.month) + 2;
    return { year: date.year, month, day: daysInMonth(date.year, month) };
  },
  '@startOfYear': (date) => ({ year: date.year, month: 1, day: 1 }),
  '@endOfYear': (date) => ({ year: date.year, month: 12, day: 31 }),
};

const UNIT_SHIFTS: Record<RollingDateUnit, (date: CivilDate, amount: number) => CivilDate> = {
  d: (date, amount) => addDays(date, amount),
  w: (date, amount) => addDays(date, amount * 7),
  m: (date, amount) => addMonths(date, amount),
  q: (date, amount) => addMonths(date, amount * 3),
  y: (date, amount) => addMonths(date, amount * 12),
};

/**
 * Resolves a parsed rolling date against the viewer's "today" (a bare
 * `YYYY-MM-DD` civil date, as produced by `civilDateInZone`) to another bare
 * `YYYY-MM-DD` civil date.
 *
 * Offset first, then anchor — see the module header.
 */
export function resolveRollingDate(parsed: ParsedRollingDate, todayCivilDate: string): string {
  const today = parseCivilDate(todayCivilDate);
  const shifted = parsed.unit
    ? UNIT_SHIFTS[parsed.unit](today, parsed.sign * parsed.amount)
    : today;
  return formatCivilDate(ANCHOR_SNAPS[parsed.anchor](shifted));
}

/**
 * Parse-and-resolve in one call. Returns `null` when `token` is not a valid
 * rolling date, so callers can distinguish "not my token" from a real date
 * without a second parse.
 */
export function resolveRollingDateToken(token: unknown, todayCivilDate: string): string | null {
  const parsed = parseRollingDateToken(token);
  return parsed ? resolveRollingDate(parsed, todayCivilDate) : null;
}

// ---------------------------------------------------------------------------
// Time-zone boundary (the only zone-aware code in the filter-variable stack)
// ---------------------------------------------------------------------------

/** The zone every resolution falls back to when nothing better is known. */
export const DEFAULT_FILTER_TIME_ZONE = 'UTC';

/**
 * True when `timeZone` is an IANA identifier this runtime can resolve.
 * `Intl.DateTimeFormat` throws `RangeError` for an unknown zone, which is the
 * only reliable cross-platform validation — there is no list to check against.
 */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    // `formatToParts` with explicit numeric fields, rather than a locale whose
    // pattern happens to be ISO-ordered: the parts are assembled here, so no
    // host's ICU locale data can reorder or re-calendar the result.
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      era: 'short',
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

interface ZonedParts extends CivilDate {
  hour: number;
  minute: number;
  second: number;
}

function zonedParts(instantMs: number, timeZone: string): ZonedParts {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const hour = Number(read('hour'));
  return {
    // A BC year would make the civil date meaningless; it cannot arise from any
    // offset this grammar accepts, but negating here keeps the arithmetic
    // honest rather than silently treating 1 BC as year 1.
    year: read('era') === 'BC' ? 1 - Number(read('year')) : Number(read('year')),
    month: Number(read('month')),
    day: Number(read('day')),
    // Some engines render midnight as hour 24 under `hour12: false`.
    hour: hour === 24 ? 0 : hour,
    minute: Number(read('minute')),
    second: Number(read('second')),
  };
}

/**
 * The civil date (`YYYY-MM-DD`) that `instant` falls on in `timeZone`. This is
 * how "today" is decided for the viewer, and it is the ONLY thing that makes a
 * rolling date zone-dependent.
 */
export function civilDateInZone(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant.getTime(), timeZone);
  return formatCivilDate(parts);
}

/** `timeZone`'s UTC offset, in ms, at the instant `instantMs`. */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = zonedParts(instantMs, timeZone);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  // The formatter has second resolution, so compare against a second-aligned
  // instant; sub-second precision is irrelevant to an offset, which is always a
  // whole number of minutes in the IANA database.
  return asIfUtc - Math.floor(instantMs / 1000) * 1000;
}

/**
 * The first instant of `civilDate` in `timeZone`, as a canonical ISO 8601 UTC
 * string (`Z`-suffixed, millisecond precision) — the platform's one date wire
 * form (see `/standards/shared/filtering.md`).
 *
 * ## Why two candidates rather than one offset lookup
 *
 * Converting a civil time to an instant is not a function of one offset,
 * because the offset itself depends on the instant. The standard fixed-point
 * pass (guess the offset at "civil time read as UTC", correct, re-read) lands
 * on the right instant for every ordinary day and for a fall-back (repeated)
 * midnight, where it correctly picks the EARLIER occurrence.
 *
 * It can overshoot on a SPRING-FORWARD midnight — a day that has no 00:00 at
 * all, which really happens (e.g. `America/Santiago`, `Asia/Beirut`,
 * `America/Havana` have all had a midnight DST jump). There the second pass can
 * land back on the previous day. So both candidates are checked against the
 * civil date they were supposed to produce, and the earliest one that actually
 * falls on that date wins: for a skipped midnight that is 01:00 local, the
 * genuine first instant of the day.
 *
 * A candidate that matches is never merely plausible — it is verified — so this
 * cannot silently return an instant belonging to a neighbouring day.
 */
export function zonedStartOfDayIso(civilDate: string, timeZone: string): string {
  const date = parseCivilDate(civilDate);
  const asIfUtc = Date.UTC(date.year, date.month - 1, date.day);

  const firstPass = asIfUtc - zoneOffsetMs(asIfUtc, timeZone);
  const secondPass = asIfUtc - zoneOffsetMs(firstPass, timeZone);

  const candidates = firstPass <= secondPass ? [firstPass, secondPass] : [secondPass, firstPass];
  for (const candidate of candidates) {
    if (civilDateInZone(new Date(candidate), timeZone) === civilDate) {
      return new Date(candidate).toISOString();
    }
  }

  // Unreachable for any zone in the IANA database: one of the two passes always
  // lands inside the day. Throwing rather than returning the closer guess keeps
  // a future database change from silently shifting a filter's bound by hours.
  throw new Error(
    `[rollingDates] Could not resolve the start of ${civilDate} in ${timeZone}`
  );
}

/**
 * The LAST instant of `civilDate` in `timeZone` (inclusive), as a canonical ISO
 * 8601 UTC string.
 *
 * Computed as "one millisecond before the start of the next day" rather than as
 * local `23:59:59.999`: on a day whose final hour is skipped by a DST jump,
 * 23:59:59.999 does not exist, and on a day extended by a fall-back it is not
 * the last instant. The next day's start is always well defined, so this is
 * exact on every day of every zone.
 */
export function zonedEndOfDayIso(civilDate: string, timeZone: string): string {
  const nextDay = formatCivilDate(addDays(parseCivilDate(civilDate), 1));
  const nextStartMs = Date.parse(zonedStartOfDayIso(nextDay, timeZone));
  return new Date(nextStartMs - 1).toISOString();
}
